import { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { FormRenderProps } from 'react-final-form';
import equal from 'fast-deep-equal';
import { connect, useDispatch } from 'react-redux';
import { useUnmount } from 'react-use';

import { FiltersModal } from 'CorePackage/FiltersModalModule/components/FiltersModal';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { CheckboxField } from 'UIPackage/MainModule/components/CheckboxField';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { ISetFilterCurrentParams } from 'CorePackage/FiltersModalModule/interfaces/ISetFilterCurrentParams';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { getUserProfileIsTest } from 'AuthPackage/MainModule/selectors/authSelectors';
import { agreementListGetData } from 'AgreementListPackage/MainModule/actions/agreementListActions';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { agreementFilterFormBooleanSelectOptions } from 'AgreementListPackage/AgreementListPanelModule/constants/agreementListFilterOptions';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IKnmTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmTypeSelectOption';
import { IYearSelectOption } from 'CorePackage/MainModule/interfaces/IYearSelectOption';
import { ITerritorialUnitSelectOption } from 'CorePackage/CatalogsModule/interfaces/ITerritorialUnitSelectOption';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import {
  getCatalogKnmTypes248SelectOptionsSelector,
  getCatalogKnmTypes294SelectOptions,
  getCatalogKnmYearsSelectOptions,
  getCatalogsTerritorialUnitsSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';

import { initialAgreementListFilter } from '../constants/initialAgreementListFilter';
import {
  IAgreementListFilterSetCurrentParams,
  agreementListFilterCreate,
  agreementListFilterGetData,
  agreementListFilterInit,
  agreementListFilterRemove,
  agreementListFilterSetCurrent,
  agreementListFilterSetMeta,
  agreementListFilterSetSwitcher,
  agreementListFilterUpdate,
} from '../actions/agreementListFilterActions';
import { mapFilterAdditionalFields } from '../common/mapFilterAdditionalFields';
import {
  initialAgreementListAdditionalFields as initialAgreementListAdditionalFieldsConstant,
  isTestAdditionalField,
} from '../constants/initialAgreementListAdditionalFields';
import {
  getAgreementListFilterCurrentFilter,
  getAgreementListFilterCurrentId,
  getAgreementListFilterFilters,
} from '../selectors/agreementListFilterSelectors';
import { IAgreementListFilter } from '../interfaces/IAgreementListFilter';
import { IAgreementListFilterWithMeta } from '../interfaces/IAgreementListFilterWithMeta';
import { IAgreementListFiltersForm } from '../interfaces/IAgreementListFiltersForm';
import { IAgreementListFilterExtraField } from '../interfaces/IAgreementListFilterField';
import { passportTypeOptions } from '../constants/passportTypeOptions';
import { agreementStatusOptions } from '../constants/agreementStatusOptions';
import styles from './AgreementListFiltersModal.module.scss';

const mapFilter = (filter: IAgreementListFilter, filterName: string): IAgreementListFilter => {
  return {
    ...filter,
    name: filterName,
  };
};

interface IAgreementListFiltersModalProps {
  visible: boolean;
  onClose: () => void;
}

interface IAgreementListFiltersModalStoreProps {
  filters: IAgreementListFilterWithMeta[];
  currentFilter: IAgreementListFilter | undefined;
  activeFilterId: number | undefined;
  isUserTest: boolean;
  isFederalLaw248: boolean;
  knmTypesSelectOptionsErknm: IKnmTypeSelectOption[];
  knmTypesSelectOptionsErp: IKnmTypeSelectOption[];
  yearsSelectOptions: IYearSelectOption[];
  currentYearSelectOption: IYearSelectOption | undefined;
  territorialUnitsSelectOptions: ITerritorialUnitSelectOption[];
}

const AgreementListFiltersModalWithoutStore = ({
  visible,
  onClose,
  activeFilterId,
  currentFilter,
  filters,
  isUserTest,
  isFederalLaw248,
  knmTypesSelectOptionsErknm,
  knmTypesSelectOptionsErp,
  yearsSelectOptions,
  currentYearSelectOption,
  territorialUnitsSelectOptions,
}: IAgreementListFiltersModalProps & IAgreementListFiltersModalStoreProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useUnmount(() => {
    dispatch(agreementListFilterSetMeta([]));
  });

  const getAgreementListData = useCallback(() => dispatch(agreementListGetData()), [dispatch]);
  const getAgreementListFilterData = useCallback(
    (id: IFilterResult['id']) => dispatch(agreementListFilterGetData(id)),
    [dispatch],
  );
  const setAgreementListFilterCurrent = useCallback(
    (params: IAgreementListFilterSetCurrentParams) => dispatch(agreementListFilterSetCurrent(params)),
    [dispatch],
  );
  const setAgreementListFilterSwitcher = useCallback(
    (value: boolean) => dispatch(agreementListFilterSetSwitcher(value)),
    [dispatch],
  );
  const createAgreementListFilter = useCallback(
    (data: IAgreementListFilter) => dispatch(agreementListFilterCreate(data)),
    [dispatch],
  );
  const updateAgreementListFilter = useCallback(
    (id: IFilterResult['id'], data: IAgreementListFilter) => dispatch(agreementListFilterUpdate(id, data)),
    [dispatch],
  );
  const removeAgreementListFilter = useCallback((id: IFilterResult['id']) => dispatch(agreementListFilterRemove(id)), [
    dispatch,
  ]);

  const initialAgreementListAdditionalFields = useMemo(() => {
    const result = initialAgreementListAdditionalFieldsConstant;

    if (isUserTest && !initialAgreementListAdditionalFieldsConstant.find(i => i.id === isTestAdditionalField.id)) {
      result.push(isTestAdditionalField);
    }

    return result;
  }, [isUserTest]);

  const mapper = useMemo(() => mapFilterAdditionalFields(isUserTest), [isUserTest]);

  const defaultTerritorialUnitSelectOption = useMemo(
    () => territorialUnitsSelectOptions.find(selectOption => selectOption.isDefault),
    [territorialUnitsSelectOptions],
  );

  const memoizedInitialAgreementListFilter: IAgreementListFilter = useMemo(
    () => ({
      name: 'Новый фильтр',
      types: passportTypeOptions,
      years: currentYearSelectOption ? [currentYearSelectOption] : [],
      ...(defaultTerritorialUnitSelectOption && { domains: [defaultTerritorialUnitSelectOption] }),
      includeDomainChild: false,
      statuses: [],
    }),
    [currentYearSelectOption, passportTypeOptions, defaultTerritorialUnitSelectOption],
  );

  const [formValues, setFormValues] = useState<IAgreementListFiltersForm>(initialAgreementListFilter);

  useEffect(() => {
    dispatch(agreementListFilterInit());
  }, [dispatch]);

  const memoizedFilterSelectFields: Omit<IAgreementListFilterExtraField, 'isSelected' | 'name'>[] = useMemo(
    () => [
      {
        id: 'isTest',
        options: agreementFilterFormBooleanSelectOptions,
      },
    ],
    [agreementFilterFormBooleanSelectOptions],
  );

  const [additionalFields, setAdditionalFields] = useState(initialAgreementListAdditionalFields);

  const onTemplateCreate = useCallback(() => {
    const newData = {
      ...initialAgreementListFilter,
      ...memoizedInitialAgreementListFilter,
    };

    setFormValues(newData);
    const filter = mapFilter(newData, 'Новый фильтр');
    const filterData: ISetFilterCurrentParams<IAgreementListFilter> = {
      current: { ...filter, isTemplateFirstCreate: true },
      currentId: undefined,
    };

    setAgreementListFilterCurrent(filterData);
    setAdditionalFields(initialAgreementListAdditionalFields);
  }, [initialAgreementListAdditionalFields, memoizedInitialAgreementListFilter, setAgreementListFilterCurrent]);

  useEffect(() => {
    const newData = {
      ...initialAgreementListFilter,
      ...memoizedInitialAgreementListFilter,
      isTemplateFirstCreate: true,
    };

    if (!equal(formValues, newData) && activeFilterId === undefined && formValues.isTemplateFirstCreate) {
      onTemplateCreate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFilterId, memoizedInitialAgreementListFilter]);

  const removeAdditionalField = (name: IAgreementListFilterExtraField['id']) => {
    setAdditionalFields(prev =>
      prev.map(f => {
        if (f.id === name) {
          return {
            ...f,
            isSelected: false,
          };
        }

        return f;
      }),
    );
  };

  const knmTypesSelectOptions = useMemo(
    () => (isFederalLaw248 ? knmTypesSelectOptionsErknm : knmTypesSelectOptionsErp),
    [isFederalLaw248, knmTypesSelectOptionsErknm, knmTypesSelectOptionsErp],
  );

  function renderFormContent({ form, values }: FormRenderProps<IAgreementListFiltersForm>) {
    const makeRemoveFilterHandler = (name: IAgreementListFilterExtraField['id']) => () => {
      removeAdditionalField(name);
      form.change(name);

      if (name === 'executionAgreementDateBetween') {
        form.change('executionAgreementDateBetweenStart');
        form.change('executionAgreementDateBetweenEnd');
      }
    };

    const clearKnmTypeIds = (value: IKnmTypeSelectOption[]) => {
      if (!value.find(i => i.value === 'isKnm')) {
        form.change('knmTypes');
      }
    };

    function isSelectInputFieldMultiClearableEqual(
      a: ISelectOption[] | null | undefined,
      b: ISelectOption[] | null | undefined,
    ) {
      const newA = a?.length === 0 ? undefined : a;
      const newB = b?.length === 0 ? undefined : b;

      return equal(newA, newB);
    }

    const primaryContent = (
      <>
        <Row>
          <FieldBlock label="Вид мероприятия" labelFontSize="small">
            <SelectInputField
              id="types"
              name="types"
              options={passportTypeOptions}
              isSearchable
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
              onChange={clearKnmTypeIds}
            />
          </FieldBlock>
        </Row>
        {values.types?.find(i => i.value === 'isKnm') && (
          <Row>
            <FieldBlock label={isFederalLaw248 ? 'Характер КНМ' : 'Вид КНМ'} labelFontSize="small">
              <SelectInputField
                id="knmTypes"
                name="knmTypes"
                options={knmTypesSelectOptions}
                isMulti
                isClearable={false}
                isEqual={equal}
                validate={makeRequired('Поле не может быть пустым')}
              />
            </FieldBlock>
          </Row>
        )}
        <Row>
          <FieldBlock label="Год проведения" labelFontSize="small">
            <SelectInputField
              id="years"
              name="years"
              options={yearsSelectOptions}
              isSearchable
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Территориальная единица" labelFontSize="small">
            <Row>
              <FieldBlock>
                <SelectInputField
                  id="domains"
                  name="domains"
                  options={territorialUnitsSelectOptions}
                  isSearchable
                  isMulti
                  isEqual={isSelectInputFieldMultiClearableEqual}
                  validate={makeRequired('Поле не может быть пустым')}
                />
              </FieldBlock>
            </Row>
            <CheckboxField
              id="includeDomainChild"
              name="includeDomainChild"
              label="Включая подчиненные организации"
              theme="system"
              type="checkbox"
            />
          </FieldBlock>
        </Row>
        <Row>
          <FieldBlock label="Статус соглашения" labelFontSize="small">
            <SelectInputField
              id="statuses"
              name="statuses"
              options={agreementStatusOptions}
              isSearchable
              isMulti
              isClearable={false}
              isEqual={equal}
              validate={makeRequired('Поле не может быть пустым')}
            />
          </FieldBlock>
        </Row>
      </>
    );
    const secondaryContent = (
      <>
        {additionalFields
          .filter(f => f.isSelected)
          .map((field, index) => (
            <Row
              key={field.id}
              border={index < additionalFields.filter(f => f.isSelected).length - 1 ? 'bottom' : undefined}
            >
              <FieldBlock
                label={field.name}
                labelFontSize="small"
                type="flex-label"
                button={<CloseButton onClick={makeRemoveFilterHandler(field.id)} />}
              >
                {field.type === 'checkbox' && (
                  <CheckboxField id={field.id} name={field.id} theme="system" type="checkbox" />
                )}
                {field.type === 'text' && (
                  <TextInputField name={field.id} type="text" validate={makeRequired('Поле не может быть пустым')} />
                )}
                {field.type === 'select' && (
                  <SelectInputField
                    id={field.id}
                    name={field.id}
                    options={memoizedFilterSelectFields.find(item => item.id === field.id)?.options}
                    isSearchable={field.isSearchable}
                    isMulti={field.isMulti}
                    isClearable={field.isClearable || false}
                    isEqual={field.isMulti && field.isClearable ? isSelectInputFieldMultiClearableEqual : equal}
                    validate={makeRequired('Поле не может быть пустым')}
                  />
                )}
                {field.type === 'date' && field.id === 'executionAgreementDateBetween' && (
                  <div className={styles.FilterFormGroup}>
                    С&nbsp;
                    <div className={styles.FieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="executionAgreementDateBetweenStart"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                    по&nbsp;
                    <div className={styles.FieldWrapper}>
                      <DatePickerField
                        className={styles.FilterFormGroupDatePickersItem}
                        name="executionAgreementDateBetweenEnd"
                        opensOnTop
                        isEqual={equal}
                        validate={makeRequired('Поле не может быть пустым')}
                      />
                    </div>
                  </div>
                )}
              </FieldBlock>
            </Row>
          ))}
      </>
    );

    return {
      primaryContent,
      secondaryContent: additionalFields.length === 0 ? undefined : secondaryContent,
    };
  }

  return (
    <FiltersModal<IAgreementListFiltersForm, IAgreementListFilter>
      visible={visible}
      initialFormValues={formValues}
      additionalFields={additionalFields}
      filters={filters}
      currentFilter={currentFilter}
      activeFilterId={activeFilterId}
      renderFormContent={renderFormContent}
      mapFilter={mapFilter}
      mapFilterAdditionalFields={mapper}
      onClose={onClose}
      onTemplateCreate={onTemplateCreate}
      setFormValues={setFormValues}
      setAdditionalFields={setAdditionalFields}
      getData={getAgreementListData}
      getFilterData={getAgreementListFilterData}
      setFilterCurrent={setAgreementListFilterCurrent}
      setFilterSwitcher={setAgreementListFilterSwitcher}
      createFilter={createAgreementListFilter}
      updateFilter={updateAgreementListFilter}
      removeFilter={removeAgreementListFilter}
    />
  );
};

const mapStateToProps = (state: IRootState): IAgreementListFiltersModalStoreProps => {
  const knmYearsSelectOptions: IYearSelectOption[] = getCatalogKnmYearsSelectOptions(state);

  const currentYear = new Date().getFullYear();
  const nextYear = String(currentYear + 1);
  const currentYearSelectOption = knmYearsSelectOptions.find(
    selectOption => selectOption.value === String(currentYear),
  );

  const nextYearSelectOption = knmYearsSelectOptions.find(selectOption => selectOption.value === nextYear);
  const yearsSelectOptions = nextYearSelectOption
    ? knmYearsSelectOptions
    : [...knmYearsSelectOptions, { id: nextYear, label: nextYear, value: nextYear }];

  return {
    activeFilterId: getAgreementListFilterCurrentId(state),
    currentFilter: getAgreementListFilterCurrentFilter(state),
    filters: getAgreementListFilterFilters(state),
    isUserTest: getUserProfileIsTest(state),
    isFederalLaw248: getLawInterface(state) === LawInterfacesEnum.FZ_248,
    knmTypesSelectOptionsErknm: getCatalogKnmTypes248SelectOptionsSelector(state)({}),
    knmTypesSelectOptionsErp: getCatalogKnmTypes294SelectOptions(state),
    yearsSelectOptions,
    currentYearSelectOption,
    territorialUnitsSelectOptions: getCatalogsTerritorialUnitsSelectOptions(state),
  };
};

export const AgreementListFiltersModal = connect(mapStateToProps)(AgreementListFiltersModalWithoutStore);
